import { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react'
import MarkDown from '~/components/Helpers/MarkDown'
import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'

interface MassiveTextProps {
  padding?: 'large-alt' | 'small-alt' | 'large' | 'small' | null | undefined
  content: {
    sectionName: string
    copy: string
    attribution?: string
    strongColour?: boolean
    backgroundColour?: AllBackgroundColours
  }
}

const section = cva('', {
  variants: {
    padding: {
      'large-alt': 'pb-xl sm:pb-2xl lg:pb-3xl',
      'small-alt': 'pb-l sm:pb-xl lg:pb-2xl pt-[40px]',
      large: 'py-xl sm:py-2xl lg:py-3xl',
      small: 'py-l sm:py-xl lg:py-2xl',
      none: 'px-0'
    }
  },
  defaultVariants: {
    padding: 'small'
  }
})

const mTextVariants = cva('', {
  variants: {
    backgroundColour: {
      'ink-blue': 'bg-blue-d text-white',
      'mid-blue': 'bg-blue-m text-white',
      'light-blue': 'bg-blue-l text-blue-d',
      chartreuse: 'bg-char text-blue-d',
      'light-purple': 'bg-purple text-blue-d',
      'light-pink': 'bg-pink text-blue-d',
      'light-aqua': 'bg-aqua text-blue-d',
      'light-orange': 'bg-orange text-blue-d',
      'light-grey': 'bg-grey-l text-blue-d dark:bg-black dark:text-white',
      white: 'bg-white dark:bg-black'
    },
    textColour: {
      'ink-blue': 'text-blue-d',
      'mid-blue': ' text-blue-m',
      chartreuse: 'text-char',
      white: 'text-white'
    }
  },
  defaultVariants: {
    backgroundColour: 'white',
    textColour: 'ink-blue'
  }
})

const MassiveText = ({ content, padding }: MassiveTextProps) => {
  const { sectionName, copy, attribution, backgroundColour } = content
  const textRef = useRef<HTMLParagraphElement>(null)
  const [fontSize, setFontSize] = useState('8vw')

  // Memoize color calculations to avoid recalculating on every render
  const { foregroundColour, strongColour } = useMemo(() => {
    const foreground =
      backgroundColour === 'ink-blue' || backgroundColour === 'mid-blue'
        ? ('white' as const)
        : ('ink-blue' as const)

    const strong = content?.strongColour
      ? backgroundColour === 'ink-blue' || backgroundColour === 'mid-blue'
        ? ('chartreuse' as const)
        : backgroundColour === 'light-grey' ||
            backgroundColour === 'chartreuse' ||
            backgroundColour === 'white'
          ? ('mid-blue' as const)
          : ('ink-blue' as const)
      : foreground

    return { foregroundColour: foreground, strongColour: strong }
  }, [backgroundColour, content?.strongColour])

  // Font size adjustment to improve performance
  const adjustFontSize = useCallback(() => {
    if (!textRef.current) return

    const container = textRef.current.closest('.\\@container')
    if (!container) return

    const containerWidth = container.clientWidth
    const targetWidth = containerWidth * 1.06

    // Cache mobile check
    const isMobile = containerWidth < 768
    const isTinyScreen = containerWidth < 360
    const basePercentage = isTinyScreen ? 0.09 : isMobile ? 0.12 : 0.15
    const buffer = 0.95

    // Calculate initial size
    let testSize = Math.min(containerWidth * basePercentage, 400)
    textRef.current.style.fontSize = `${testSize}px`

    // Adjust if text overflows
    const textWidth = textRef.current.scrollWidth
    if (textWidth > targetWidth) {
      const scale = targetWidth / textWidth
      testSize = testSize * scale * buffer
    }

    const minSize = isTinyScreen ? 16 : isMobile ? 24 : 48
    const maxSize = isTinyScreen ? 80 : isMobile ? 200 : 400

    setFontSize(`${Math.max(minSize, Math.min(testSize, maxSize))}px`)
  }, [])

  useEffect(() => {
    adjustFontSize()
    window.addEventListener('resize', adjustFontSize)
    return () => {
      window.removeEventListener('resize', adjustFontSize)
    }
  }, [copy, adjustFontSize])

  return (
    <section
      aria-label={sectionName}
      className={cn(
        mTextVariants({ backgroundColour: backgroundColour, textColour: null })
      )}
    >
      <div
        className={cn(
          mTextVariants({ backgroundColour: null, textColour: strongColour }),
          section({
            padding: padding
          }),
          'space-y-m sm:space-y-l md:space-y-xl overflow-x-hidden'
        )}
      >
        <div className='@container'>
          <MarkDown
            content={copy}
            className='max-w-none'
            components={{
              p: ({ children }: { children?: React.ReactNode }) => (
                <p
                  ref={textRef}
                  className='-ml-[6%] w-[106%] text-right font-serif whitespace-nowrap'
                  style={{
                    fontSize: fontSize
                  }}
                >
                  {children}
                </p>
              )
            }}
          />
        </div>
        {attribution && (
          <div
            className={cn(
              mTextVariants({
                backgroundColour: null,
                textColour: foregroundColour
              }),
              'container flex w-auto items-center justify-end font-serif'
            )}
          >
            <p className='text-h2-sm sm:text-h2-md lg:text-h2-lg font-normal'>
              {attribution}
            </p>
          </div>
        )}
      </div>
    </section>
  )
}

export default memo(MassiveText)
